import { sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";

import * as api from "../../../api";
import useCompetition, {
  useCompetitionRoutes,
} from "../../../hooks/useCompetition";
import { useVenueMaps } from "../../../hooks/useVenueRoutes";
import { useJudgeAuthWall } from "../../../modules/onboarding/components/hooks/useAuthWall";
import { ApiRoute } from "../../../types";
import getCompetitionRouteDisplay from "../../../utils/getCompetitionRouteDisplay";

export default function useRenumberRoutesScreen() {
  const { competitionUUID } = useParams();
  useJudgeAuthWall(`/${competitionUUID}/manage-routes/categories`);
  const { competition, ...competitionReq } = useCompetition(competitionUUID);
  const venueUUID = competition?.venues?.[0]?.uuid;

  // eslint-disable-next-line prefer-const
  let { routes, ...routesReq } = useCompetitionRoutes({
    venueUUID,
    competitionUUID: competition?.uuid,
  });
  routes = sortBy(routes, (x) => x.circuitNumber);
  const [selectedCategoryUUID, setSelectedCategoryUUID] = useState<
    string | undefined
  >();
  const [selectedRouteUUID, _setSelectedRouteUUID] = useState<
    string | null | undefined
  >();
  const selectedCategories = useMemo(
    () =>
      competition?.stages?.[0]?.categories.filter(
        (x) => x.uuid === selectedCategoryUUID
      ),
    [selectedCategoryUUID, competition]
  );

  const filteredRoutes = useMemo(
    () =>
      selectedCategoryUUID === undefined
        ? routes
        : routes?.filter((x) =>
            x.competitionRouteGroups.some((y) =>
              y.categories?.includes(selectedCategoryUUID)
            )
          ),
    [routes, selectedCategoryUUID]
  );

  const { maps } = useVenueMaps(competition?.venues?.[0]?.uuid);

  const handleRenumber = useCallback(
    ({
      routeUUID,
      categoryUUID,
      number,
    }: {
      routeUUID: string;
      categoryUUID: string;
      number: number;
    }) => {
      if (!competitionUUID) {
        return;
      }

      const competitionRouteGroup = competition?.routeGroups.find((x) =>
        x.categories.includes(categoryUUID)
      );
      if (!competitionRouteGroup) {
        return;
      }

      const categoryRoutes =
        routes
          ?.filter((route) =>
            route.competitionRouteGroups.some((x) =>
              x.categories?.includes(categoryUUID)
            )
          )
          .map((route) => ({
            ...route,
            display: getCompetitionRouteDisplay({
              route,
              categoryUUID,
              routeGroups: route.competitionRouteGroups,
              useFallback: false,
            }),
          })) || [];

      const oldNumber = categoryRoutes.find((x) => x.uuid === routeUUID)
        ?.display.number;

      const routesWithNewNumber = categoryRoutes.filter(
        (x) => x.display.number === number
      );

      const updates = [
        {
          route: routeUUID,
          number: number,
          routeGroup: competitionRouteGroup.uuid,
        },
        ...routesWithNewNumber.map((x) => ({
          route: x.uuid,
          number: oldNumber || null,
          routeGroup: competitionRouteGroup.uuid,
        })),
        ...categoryRoutes
          .filter((x) => x.uuid !== routeUUID && x.display.number !== number)
          .map((route) => ({
            route: route.uuid,
            number: route.display.number,
            routeGroup: competitionRouteGroup.uuid,
          })),
      ];

      mutate(
        `/venues/${venueUUID}/routes`,
        async (venueRoutes?: ApiRoute[]) => {
          if (!venueRoutes) {
            return undefined;
          }
          const updatedRoutes = venueRoutes.map((route) => {
            const update = updates.find((x) => x.route === route.uuid);
            return {
              ...route,
              displayRouteGroupMapping: {
                ...route.displayRouteGroupMapping,
                ...(update &&
                update.number !== undefined &&
                update.number !== null
                  ? {
                      [update.routeGroup]: {
                        number: update.number,
                        name: update.number
                          ? competitionRouteGroup.name + update.number
                          : "",
                      },
                    }
                  : {}),
              },
            };
          });
          await api.updateRouteNumbers(competitionUUID, updates);
          // eslint-disable-next-line no-console
          console.log({ updatedRoutes });
          return updatedRoutes;
        }
      );
    },
    [competition?.routeGroups, competitionUUID, routes, venueUUID]
  );

  const setSelectedRouteUUID = (uuid: string | null) => {
    _setSelectedRouteUUID(uuid);
    if (!selectedCategoryUUID && uuid) {
      const route = routes?.find((x) => x.uuid === uuid);
      if (!route) {
        return;
      }
      const nextMissingCategoryUUID = route.competitionRouteGroups.filter(
        (x) => !route.displayRouteGroupMapping[x.uuid]
      )[0]?.categories?.[0];
      if (nextMissingCategoryUUID) {
        setSelectedCategoryUUID(nextMissingCategoryUUID);
      }
    }
  };

  return {
    routes: filteredRoutes,
    maps,
    isLoading:
      !routes && (routesReq.isValidating || competitionReq.isValidating),
    selectedCategoryUUID,
    selectedCategories,
    setSelectedCategoryUUID,
    selectedRouteUUID,
    setSelectedRouteUUID,
    competition,
    handleRenumber,
  };
}
